import { useSelector } from 'react-redux';

// Utils
import {
  getTransformedFormFieldProps,
  getValidationFunction,
} from 'app/modules/fincenCtr/helpers';
import { validateLength } from 'app/shared/utils/validation';

// Constants
import {
  PARTY_NAME_ARRAY_SCHEMA,
  ENTITY_LAST_NAME_UNKNOWN_INDICATOR_INPUT,
  RAW_ENTITY_INDIVIDUAL_LAST_NAME_INPUT,
  FIRST_NAME_UNKNOWN_INDICATOR_INPUT,
  RAW_INDIVIDUAL_FIRST_NAME_INPUT,
  RAW_INDIVIDUAL_MIDDLE_NAME_INPUT,
  RAW_INDIVIDUAL_NAME_SUFFIX_INPUT,
  RAW_PARTY_FULL_NAME_INPUT,
} from 'app/modules/fincenCtr/schemas/parties/partyName';

// Selectors
import { selectValidationErrors } from 'app/modules/fincenCtr/selectors';

// Components
import { U21FormField } from 'app/shared/u21-ui/components';
import {
  ActionsContainer,
  Block2,
  Block2MainField,
  FieldLabel,
  InlineFieldLabel,
} from 'app/modules/fincenCtr/components/StylingUtils';

// Models
import { useField } from 'react-final-form';
import { ActivityPartyTypeCode } from 'app/modules/fincenCtr/enums';
import { getIn } from 'final-form';
import { ValidationErrors } from 'app/modules/fincenCtr/models';

interface OwnProps {
  fieldPath: string;
  partyType: ActivityPartyTypeCode;
  readOnly?: boolean;
  legalNameDescription?: string;
  akaDescription?: string;
  showAlternateName?: boolean;
  isEntity?: boolean;
}

export const PERSON_PARTY_TYPE_CODES = new Set([
  ActivityPartyTypeCode.PERSON_CONDUCTING_TXN_ON_OWN_BEHALF,
  ActivityPartyTypeCode.PERSON_CONDUCTING_TXN_FOR_ANOTHER,
  ActivityPartyTypeCode.PERSON_ON_WHOSE_BEHALF_TXN_WAS_CONDUCTED,
  ActivityPartyTypeCode.COMMON_CARRIER,
]);

interface SubformProps {
  fieldPath: string;
  validationErrors: ValidationErrors;
  readOnly?: boolean;
  description?: string;
  isEntity?: boolean;
}

const AlternateNameInput = ({
  fieldPath,
  readOnly,
  validationErrors,
  isPerson,
  description,
}: SubformProps & { isPerson: boolean }) => {
  const baseConfig = isPerson
    ? RAW_ENTITY_INDIVIDUAL_LAST_NAME_INPUT
    : RAW_PARTY_FULL_NAME_INPUT;
  const fullNameConfig = {
    ...baseConfig,
    label: 'Alternate name',
    required: false,
    validate: undefined,
    name: `${fieldPath}.${baseConfig.name}`,
    fieldProps: {
      id: `${fieldPath}.${baseConfig.name}`,
    },
  };
  return (
    <Block2 key={fullNameConfig.name}>
      <FieldLabel htmlFor={fullNameConfig.name} description={description}>
        Alternate name
      </FieldLabel>
      <Block2MainField>
        <U21FormField<string | undefined>
          key={fullNameConfig.name}
          {...getTransformedFormFieldProps(fullNameConfig)}
          {...getValidationFunction(fullNameConfig, validationErrors)}
          label={undefined}
          validate={(value) => validateLength(150, value)}
          disabled={readOnly}
        />
      </Block2MainField>
    </Block2>
  );
};

const InstitutionPartyNameForm = ({
  fieldPath,
  validationErrors,
  readOnly,
  description,
}: SubformProps) => {
  const canEdit = !readOnly; // TODO add permissions check
  const fullNameConfig = {
    ...RAW_PARTY_FULL_NAME_INPUT,
    name: `${fieldPath}.${RAW_PARTY_FULL_NAME_INPUT.name}`,
    fieldProps: {
      id: `${fieldPath}.${RAW_PARTY_FULL_NAME_INPUT.name}`,
    },
  };
  return (
    <Block2 key={fullNameConfig.name}>
      <FieldLabel
        description={description}
        htmlFor={fullNameConfig.name}
        required
      >
        Legal name
      </FieldLabel>
      <Block2MainField>
        <U21FormField<string | undefined>
          key={fullNameConfig.name}
          {...getTransformedFormFieldProps(fullNameConfig)}
          {...getValidationFunction(fullNameConfig, validationErrors)}
          disabled={!canEdit}
          label={undefined}
          validate={(value) => validateLength(150, value, true)}
        />
      </Block2MainField>
    </Block2>
  );
};

const PersonPartyNameForm = ({
  fieldPath,
  validationErrors,
  readOnly,
  isEntity,
}: SubformProps) => {
  const canEdit = !readOnly; // TODO add permissions check
  // Field configs
  const unknownLastNameConfig = {
    ...ENTITY_LAST_NAME_UNKNOWN_INDICATOR_INPUT,
    name: `${fieldPath}.${ENTITY_LAST_NAME_UNKNOWN_INDICATOR_INPUT.name}`,
    fieldProps: { label: 'Unknown' },
  };

  const unknownFirstNameConfig = {
    ...FIRST_NAME_UNKNOWN_INDICATOR_INPUT,
    name: `${fieldPath}.${FIRST_NAME_UNKNOWN_INDICATOR_INPUT.name}`,
    fieldProps: { label: 'Unknown' },
  };

  const middleNameFieldConfig = {
    ...RAW_INDIVIDUAL_MIDDLE_NAME_INPUT,
    name: `${fieldPath}.${RAW_INDIVIDUAL_MIDDLE_NAME_INPUT.name}`,
    fieldProps: {
      id: `${fieldPath}.${RAW_INDIVIDUAL_MIDDLE_NAME_INPUT.name}`,
    },
  };
  const suffixFieldConfig = {
    ...RAW_INDIVIDUAL_NAME_SUFFIX_INPUT,
    name: `${fieldPath}.${RAW_INDIVIDUAL_NAME_SUFFIX_INPUT.name}`,
    fieldProps: {
      id: `${fieldPath}.${RAW_INDIVIDUAL_NAME_SUFFIX_INPUT.name}`,
    },
  };

  // Subscriptions
  const {
    input: { value: unknownLastName },
  } = useField<boolean>(unknownLastNameConfig.name);
  const {
    input: { value: unknownFirstName },
  } = useField<boolean>(unknownFirstNameConfig.name);

  const lastNameFieldConfig = {
    ...RAW_ENTITY_INDIVIDUAL_LAST_NAME_INPUT,
    name: `${fieldPath}.${RAW_ENTITY_INDIVIDUAL_LAST_NAME_INPUT.name}`,
    required: !unknownLastName,
    label: isEntity ? 'Entity name' : 'Last name',
    fieldProps: {
      id: `${fieldPath}.${RAW_ENTITY_INDIVIDUAL_LAST_NAME_INPUT.name}`,
    },
  };

  const firstNameFieldConfig = {
    ...RAW_INDIVIDUAL_FIRST_NAME_INPUT,
    name: `${fieldPath}.${RAW_INDIVIDUAL_FIRST_NAME_INPUT.name}`,
    required: !unknownFirstName,
    fieldProps: {
      id: `${fieldPath}.${RAW_INDIVIDUAL_FIRST_NAME_INPUT.name}`,
    },
  };

  return (
    <>
      {/* Last Name */}
      <Block2>
        <FieldLabel
          description="Item 4"
          htmlFor={lastNameFieldConfig.name}
          required={!unknownLastName}
        >
          {lastNameFieldConfig.label}
        </FieldLabel>
        <Block2MainField>
          <U21FormField<string | undefined>
            key={lastNameFieldConfig.name}
            {...getTransformedFormFieldProps(lastNameFieldConfig)}
            {...getValidationFunction(lastNameFieldConfig, validationErrors)}
            disabled={unknownLastName || !canEdit}
            label={undefined}
            validate={(value, allValues) =>
              validateLength(
                150,
                value,
                true,
                getIn(allValues, unknownLastNameConfig.name),
              )
            }
          />
        </Block2MainField>
        <ActionsContainer>
          <U21FormField
            key={unknownLastNameConfig.name}
            {...getTransformedFormFieldProps(unknownLastNameConfig)}
            {...getValidationFunction(unknownLastNameConfig, validationErrors)}
            disabled={!canEdit}
            label={undefined}
          />
        </ActionsContainer>
      </Block2>

      {!isEntity && (
        <>
          <Block2>
            <FieldLabel
              description="Item 5"
              htmlFor={firstNameFieldConfig.name}
              required={!unknownFirstName}
            >
              {firstNameFieldConfig.label}
            </FieldLabel>
            <Block2MainField>
              <U21FormField<string | undefined>
                key={firstNameFieldConfig.name}
                {...getTransformedFormFieldProps(firstNameFieldConfig)}
                {...getValidationFunction(
                  firstNameFieldConfig,
                  validationErrors,
                )}
                disabled={unknownFirstName || !canEdit}
                label={undefined}
                validate={(value, allValues) =>
                  validateLength(
                    35,
                    value,
                    true,
                    getIn(allValues, unknownFirstNameConfig.name),
                  )
                }
              />
            </Block2MainField>
            <ActionsContainer>
              <U21FormField
                key={unknownFirstNameConfig.name}
                {...getTransformedFormFieldProps(unknownFirstNameConfig)}
                {...getValidationFunction(
                  unknownFirstNameConfig,
                  validationErrors,
                )}
                disabled={!canEdit}
                label={undefined}
              />
            </ActionsContainer>
          </Block2>

          <Block2>
            <FieldLabel
              description="Item 6"
              htmlFor={middleNameFieldConfig.name}
            >
              {middleNameFieldConfig.label}
            </FieldLabel>
            <Block2MainField>
              <U21FormField<string | undefined>
                key={middleNameFieldConfig.name}
                {...getTransformedFormFieldProps(middleNameFieldConfig)}
                {...getValidationFunction(
                  middleNameFieldConfig,
                  validationErrors,
                )}
                disabled={!canEdit}
                label={undefined}
                validate={(value) => {
                  if (value && value.length > 35) {
                    return 'Max 35 characters allowed';
                  }
                  return undefined;
                }}
              />
            </Block2MainField>
            <InlineFieldLabel htmlFor={suffixFieldConfig.name}>
              {suffixFieldConfig.label}
            </InlineFieldLabel>
            <U21FormField<string | undefined>
              key={suffixFieldConfig.name}
              {...getTransformedFormFieldProps(suffixFieldConfig)}
              {...getValidationFunction(suffixFieldConfig, validationErrors)}
              disabled={!canEdit}
              label={undefined}
              validate={(value) => {
                if (value && value.length > 35) {
                  return 'Max 35 characters allowed';
                }
                return undefined;
              }}
            />
          </Block2>
        </>
      )}
    </>
  );
};

export const PartyNameForm = ({
  fieldPath,
  partyType,
  readOnly = false,
  legalNameDescription,
  akaDescription,
  showAlternateName = true,
  isEntity,
}: OwnProps) => {
  const validationErrors = useSelector(selectValidationErrors);
  const nameWithFieldPath = `${fieldPath}.${PARTY_NAME_ARRAY_SCHEMA.name}`;
  const isPersonForm = PERSON_PARTY_TYPE_CODES.has(partyType);

  const legalNamePath = `${nameWithFieldPath}[0]`;
  const otherNamePath = `${nameWithFieldPath}[1]`;

  return (
    <>
      {isPersonForm && (
        <PersonPartyNameForm
          fieldPath={legalNamePath}
          validationErrors={validationErrors}
          isEntity={isEntity}
          readOnly={readOnly}
        />
      )}
      {!isPersonForm && (
        <InstitutionPartyNameForm
          fieldPath={legalNamePath}
          validationErrors={validationErrors}
          description={legalNameDescription}
          readOnly={readOnly}
        />
      )}
      {showAlternateName && (
        <AlternateNameInput
          fieldPath={otherNamePath}
          validationErrors={validationErrors}
          isPerson={isPersonForm}
          description={akaDescription}
          readOnly={readOnly}
        />
      )}
    </>
  );
};
